import { computed, ref } from 'vue'

type WebphonePermissionState =
  | 'granted'
  | 'denied'
  | 'prompt'
  | 'unsupported'
  | 'unavailable'
  | 'unknown'
type MicrophoneAccessStatus = 'granted' | 'denied' | 'unavailable' | 'unsupported' | 'error'

export type MicrophoneAccessResult = {
  allowed: boolean
  status: MicrophoneAccessStatus
  error?: unknown
}

const microphonePermission = ref<WebphonePermissionState>('unknown')
const browserMicrophonePermission = ref<WebphonePermissionState>('unknown')
const soundPermission = ref<WebphonePermissionState>('unknown')

let permissionStatusHandle: PermissionStatus | null = null
let permissionListenerAttached = false

const normalizePermissionState = (state?: string | null): WebphonePermissionState => {
  if (state === 'granted' || state === 'denied' || state === 'prompt') {
    return state
  }

  return 'unknown'
}

const stopTracks = (stream: MediaStream) => {
  stream.getTracks().forEach((track) => track.stop())
}

const checkSoundPermission = () => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    soundPermission.value = 'unsupported'
    return soundPermission.value
  }

  const getAutoplayPolicy = (
    navigator as Navigator & {
      getAutoplayPolicy?: (type: 'mediaelement' | 'audiocontext') => string
    }
  ).getAutoplayPolicy

  if (typeof getAutoplayPolicy !== 'function') {
    soundPermission.value = 'unknown'
    return soundPermission.value
  }

  try {
    const mediaElementPolicy = getAutoplayPolicy.call(navigator, 'mediaelement')
    const audioContextPolicy = getAutoplayPolicy.call(navigator, 'audiocontext')
    const policies = [mediaElementPolicy, audioContextPolicy]

    if (policies.every((policy) => policy === 'allowed')) {
      soundPermission.value = 'granted'
      return soundPermission.value
    }

    if (policies.some((policy) => policy === 'disallowed')) {
      soundPermission.value = 'denied'
      return soundPermission.value
    }

    if (policies.some((policy) => policy === 'allowed-muted')) {
      soundPermission.value = 'prompt'
      return soundPermission.value
    }
  } catch {
    soundPermission.value = 'unknown'
    return soundPermission.value
  }

  soundPermission.value = 'unknown'
  return soundPermission.value
}

const syncPermissionStatusListener = async () => {
  if (typeof window === 'undefined' || !('permissions' in navigator)) return null

  try {
    if (!permissionStatusHandle) {
      permissionStatusHandle = await navigator.permissions.query({
        name: 'microphone' as PermissionName,
      })
    }

    const permissionState = normalizePermissionState(permissionStatusHandle.state)
    browserMicrophonePermission.value = permissionState
    if (permissionState === 'denied') {
      microphonePermission.value = permissionState
    }

    if (permissionListenerAttached) return permissionState

    permissionStatusHandle.addEventListener('change', () => {
      const nextPermissionState = normalizePermissionState(permissionStatusHandle?.state)
      browserMicrophonePermission.value = nextPermissionState
      if (nextPermissionState === 'denied') {
        microphonePermission.value = nextPermissionState
        return
      }

      void checkMicrophonePermission()
    })
    permissionListenerAttached = true
    return permissionState
  } catch {
    if (microphonePermission.value === 'unknown') {
      microphonePermission.value = 'unsupported'
    }

    return null
  }
}

const checkMicrophonePermission = async () => {
  if (typeof window === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    microphonePermission.value = 'unsupported'
    return microphonePermission.value
  }

  const permissionState = await syncPermissionStatusListener()

  if (permissionState === 'denied') {
    microphonePermission.value = 'denied'
    return microphonePermission.value
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    const hasAudioInput = devices.some((device) => device.kind === 'audioinput')
    microphonePermission.value = hasAudioInput
      ? permissionState === 'granted' || microphonePermission.value === 'granted'
        ? 'granted'
        : 'prompt'
      : 'unavailable'
  } catch {
    microphonePermission.value =
      microphonePermission.value === 'unsupported' ? 'unsupported' : 'prompt'
  }

  return microphonePermission.value
}

const ensureMicrophoneAccess = async (): Promise<MicrophoneAccessResult> => {
  if (typeof window === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
    microphonePermission.value = 'unsupported'
    return { allowed: false, status: 'unsupported' }
  }

  try {
    // La prueba no debe reservar el micrófono: cerramos el stream de inmediato.
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    stopTracks(stream)
    microphonePermission.value = 'granted'
    return { allowed: true, status: 'granted' }
  } catch (error) {
    const errorName = error instanceof DOMException ? error.name : ''

    if (errorName === 'NotAllowedError' || errorName === 'SecurityError') {
      microphonePermission.value = 'denied'
      return { allowed: false, status: 'denied', error }
    }

    if (errorName === 'NotFoundError' || errorName === 'OverconstrainedError') {
      microphonePermission.value = 'unavailable'
      return { allowed: false, status: 'unavailable', error }
    }

    microphonePermission.value = 'unknown'
    return { allowed: false, status: 'error', error }
  }
}

const checkPermissions = async () => {
  await checkMicrophonePermission()
  checkSoundPermission()
}

export const useWebphonePermissions = () => {
  const isMicrophoneAllowed = computed(() => microphonePermission.value === 'granted')
  const hasRequiredPermissions = computed(() => isMicrophoneAllowed.value)
  const shouldBlockWebphone = computed(() => !hasRequiredPermissions.value)
  const missingPermissions = computed(() => ({
    microphone: !isMicrophoneAllowed.value,
    sound: false,
  }))
  const tooltipKey = computed(() => {
    if (microphonePermission.value === 'unavailable') {
      return 'common.webphone_access.tooltip_microphone_unavailable'
    }

    if (missingPermissions.value.microphone) {
      return 'common.webphone_access.tooltip_microphone'
    }

    return 'common.webphone_access.tooltip'
  })

  return {
    microphonePermission,
    browserMicrophonePermission,
    soundPermission,
    hasRequiredPermissions,
    shouldBlockWebphone,
    missingPermissions,
    tooltipKey,
    checkPermissions,
    checkMicrophonePermission,
    ensureMicrophoneAccess,
    checkSoundPermission,
  }
}
